import { Trans } from 'react-i18next';
import { Box } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import {
  CANNOT_INSERT_UPDATE_ACTIVATE_ENTITY,
  CODE_EXECUTION_FAILED,
  CONTEXT_VARIABLE_NOT_FOUND_WHILE_STORING_DECODED_JWT,
  CONTEXT_VARIABLE_TYPE_MISMATCH,
  CV_NAME_NOT_FOUND_IN_DECODED_JWT,
  EMAIL_CONVERSATION_MISSING_CONTEXT_VARIABLES,
  EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE,
  EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE_TRANSLATION,
  FAILED_TO_DECODE_JWT,
  FIELD_INTEGRITY_EXCEPTION,
  FRESHCHAT_WIDGET_NOT_FOUND_ERROR,
  getHowToFixHandoffMessage,
  HALOCRM_API_ERROR,
  INSUFFICIENT_ACCESS_ON_CROSS_REFERENCE_ENTITY,
  INVALID_CROSS_REFERENCE_KEY,
  JIRA_SERVICE_MANAGEMENT_REQUEST_CREATION_FAILED,
  JMESPATH_QUERY_EVALUATION_FAILED,
  KUSTOMER_WIDGET_NOT_FOUND_ERROR,
  MALFORMED_ID,
  MCP_TOOL_CALL_FAILED,
  MISSING_CONTEXT_VARIABLE,
  MISSING_ZENDESK_TICKET_FIELD,
  NO_VALID_CONDITION,
  SALESFORCE_CHAT_BUTTON_NOT_FOUND_ERROR,
  SALESFORCE_MESSAGING_WIDGET_NOT_FOUND_ERROR,
  SALESFORCE_UPLOAD_ATTACHMENT_ERROR,
  SALESFORCE_WIDGET_NOT_FOUND_ERROR,
  SNAPENGAGE_WIDGET_NOT_FOUND_ERROR,
  TICKET_FETCH_FAILED,
  TICKET_UPDATE_STEP_ERROR,
  TRIAGE_FIELD_NOT_WRITABLE,
  TRIAGE_FIELD_TYPE_MISMATCH,
  TRIAGE_FIELD_VALIDATION_RULE_REJECTED,
  TRIAGE_HELPDESK_REJECTED_VALUE,
  TRIAGE_OUTPUT_VALUE_CAST_FAILED,
  TRIAGE_PRODUCT,
  TRIAGE_REQUIRED_FIELD_MISSING,
  TRIAGE_RESTRICTED_PICKLIST_VALUE_REJECTED,
  TRIAGE_TICKET_CLOSED_FOR_UPDATES,
  TRIAGE_TICKET_FETCH_FAILED,
  TRIAGE_VALUE_NOT_A_CONFIGURED_OPTION,
  WEBHOOK_REQUEST_VALIDATION_ERROR,
  ZENDESK_API_ERROR,
  ZENDESK_MESSAGING_WIDGET_NOT_FOUND_ERROR,
  ZENDESK_REQUESTER_EMAIL_INVALID,
  ZENDESK_REQUESTER_EMAIL_IS_SUPPORT_ADDRESS,
  ZENDESK_REQUESTER_EMAIL_SUSPENDED,
  ZENDESK_VALUE_INVALID_FORMAT,
  ZENDESK_WIDGET_NOT_FOUND_ERROR,
} from '../utils';
import { AssistDeepLink } from './AssistDeepLink';
import type { FontVariant } from './fontVariant';
import { HowToFixSection } from './HowToFixSection';
import { MissingContextVariableFix } from './MissingContextVariableFix';
import { MissMatchContextVariableFix } from './MissMatchContextVariableFix';
import { NoValidConditionFix } from './NoValidConditionFix';
import { PlainTextFix } from './PlainTextFix';
import { TriageErrorFix } from './TriageErrorFix';
import i18n from 'src/locales';
import {
  BaseErrorPayload,
  ErrorQueryEvent,
  MissingContextVariablePayload,
  MissMatchContextVariableTypePayload,
} from 'src/types/ErrorEventTypes';

const t = i18n.getFixedT(null, 'events');

export const getRecommendedFix = (
  error: ErrorQueryEvent,
  fontVariant: FontVariant = 'font16',
) => {
  if (error.product === 'Solve Widget') {
    return getSolveWidgetRecommendedFix(error, fontVariant);
  }
  if (error.product === 'Solve Email') {
    return getSolveEmailRecommendedFix(error, fontVariant);
  }
  if (error.product === 'Integrations') {
    return getIntegrationsRecommendedFix(error, fontVariant);
  }
  if (error.product === 'Assist') {
    return getAssistRecommendedFix(error, fontVariant);
  }
  if (error.product === TRIAGE_PRODUCT) {
    return getTriageRecommendedFix(error, fontVariant);
  }
};

const getSolveWidgetRecommendedFix = (
  error: ErrorQueryEvent,
  fontVariant: FontVariant = 'font16',
) => {
  switch (error.error_message) {
    case MISSING_CONTEXT_VARIABLE:
      return (
        <MissingContextVariableFix
          channel={error.channel}
          fontVariant={fontVariant}
          payload={error.error_payload as MissingContextVariablePayload}
        />
      );
    case CONTEXT_VARIABLE_TYPE_MISMATCH:
      return (
        <MissMatchContextVariableFix
          fontVariant={fontVariant}
          payload={error.error_payload as MissMatchContextVariableTypePayload}
        />
      );
    case NO_VALID_CONDITION:
      return (
        <NoValidConditionFix
          channel={error.channel}
          fontVariant={fontVariant}
          payload={error.error_payload as MissingContextVariablePayload}
        />
      );
    case ZENDESK_REQUESTER_EMAIL_INVALID:
      return (
        <PlainTextFix
          fontVariant={fontVariant}
          text={t('recommendedFix.solveWidget.zendeskRequesterEmailInvalid')}
        />
      );
    case ZENDESK_REQUESTER_EMAIL_IS_SUPPORT_ADDRESS:
      return (
        <PlainTextFix
          fontVariant={fontVariant}
          text={t(
            'recommendedFix.solveWidget.zendeskRequesterEmailIsSupportAddress',
          )}
        />
      );
    case ZENDESK_REQUESTER_EMAIL_SUSPENDED:
      return (
        <PlainTextFix
          fontVariant={fontVariant}
          text={t('recommendedFix.solveWidget.zendeskRequesterEmailSuspended')}
        />
      );
    case ZENDESK_VALUE_INVALID_FORMAT:
      return (
        <PlainTextFix
          fontVariant={fontVariant}
          text={t('recommendedFix.solveWidget.zendeskValueInvalidFormat')}
        />
      );
    case CANNOT_INSERT_UPDATE_ACTIVATE_ENTITY:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.cannotInsertUpdateActivateEntity.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.cannotInsertUpdateActivateEntity.step2',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.cannotInsertUpdateActivateEntity.step3',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.cannotInsertUpdateActivateEntity.step4',
            )}
          </Typography>
        </HowToFixSection>
      );
    case INSUFFICIENT_ACCESS_ON_CROSS_REFERENCE_ENTITY:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.insufficientAccessOnCrossReferenceEntity.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.insufficientAccessOnCrossReferenceEntity.step2',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.insufficientAccessOnCrossReferenceEntity.step3',
            )}
          </Typography>
        </HowToFixSection>
      );
    case MALFORMED_ID:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.malformedId.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.malformedId.step2')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.malformedId.step3')}
          </Typography>
        </HowToFixSection>
      );
    case INVALID_CROSS_REFERENCE_KEY:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.invalidCrossReferenceKey.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.invalidCrossReferenceKey.step2')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.invalidCrossReferenceKey.step3')}
          </Typography>
        </HowToFixSection>
      );
    case FIELD_INTEGRITY_EXCEPTION:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.fieldIntegrityException.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.fieldIntegrityException.step2')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.fieldIntegrityException.step3')}
          </Typography>
        </HowToFixSection>
      );
    case SALESFORCE_MESSAGING_WIDGET_NOT_FOUND_ERROR:
    case SALESFORCE_CHAT_BUTTON_NOT_FOUND_ERROR:
    case SALESFORCE_WIDGET_NOT_FOUND_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.salesforceWidgetNotFound.step1', {
              message: getHowToFixHandoffMessage('Salesforce widget'),
            })}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.salesforceWidgetNotFound.step2')}
          </Typography>
        </HowToFixSection>
      );
    case ZENDESK_MESSAGING_WIDGET_NOT_FOUND_ERROR:
    case ZENDESK_WIDGET_NOT_FOUND_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.zendeskWidgetNotFound.step1', {
              message: getHowToFixHandoffMessage('Zendesk widget'),
            })}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.zendeskWidgetNotFound.step2')}
          </Typography>
        </HowToFixSection>
      );

    case FRESHCHAT_WIDGET_NOT_FOUND_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.freshchatWidgetNotFound.step1', {
              message: getHowToFixHandoffMessage('Freshchat widget'),
            })}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.freshchatWidgetNotFound.step2')}
          </Typography>
        </HowToFixSection>
      );
    case KUSTOMER_WIDGET_NOT_FOUND_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.kustomerWidgetNotFound.step1', {
              message: getHowToFixHandoffMessage('Kustomer widget'),
            })}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.kustomerWidgetNotFound.step2')}
          </Typography>
        </HowToFixSection>
      );
    case SNAPENGAGE_WIDGET_NOT_FOUND_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.snapengageWidgetNotFound.step1', {
              message: getHowToFixHandoffMessage('SnapEngage widget'),
            })}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.snapengageWidgetNotFound.step2')}
          </Typography>
        </HowToFixSection>
      );
    case FAILED_TO_DECODE_JWT:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.failedToDecodeJwt.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.failedToDecodeJwt.step2')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.failedToDecodeJwt.step3')}
          </Typography>
        </HowToFixSection>
      );
    case CV_NAME_NOT_FOUND_IN_DECODED_JWT:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.cvNameNotFoundInDecodedJwt.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.cvNameNotFoundInDecodedJwt.step2')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.cvNameNotFoundInDecodedJwt.step3')}
          </Typography>
        </HowToFixSection>
      );
    case CONTEXT_VARIABLE_NOT_FOUND_WHILE_STORING_DECODED_JWT:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.contextVariableNotFoundWhileStoringDecodedJwt.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.contextVariableNotFoundWhileStoringDecodedJwt.step2',
            )}
          </Typography>
        </HowToFixSection>
      );
    case HALOCRM_API_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveEmail.halocrmApiError')}
          </Typography>
        </HowToFixSection>
      );
    case SALESFORCE_UPLOAD_ATTACHMENT_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.salesforceUploadAttachmentError.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.salesforceUploadAttachmentError.step2',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.salesforceUploadAttachmentError.step3',
            )}
          </Typography>
        </HowToFixSection>
      );
    case MCP_TOOL_CALL_FAILED:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.mcpToolCallFailed.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.mcpToolCallFailed.step2')}
          </Typography>
        </HowToFixSection>
      );
    case JIRA_SERVICE_MANAGEMENT_REQUEST_CREATION_FAILED:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.jiraServiceManagementRequestCreationFailed.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.jiraServiceManagementRequestCreationFailed.step2',
            )}
          </Typography>
        </HowToFixSection>
      );
    case JMESPATH_QUERY_EVALUATION_FAILED:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.jmespathQueryEvaluationFailed.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveWidget.jmespathQueryEvaluationFailed.step2',
            )}
          </Typography>
        </HowToFixSection>
      );
    case CODE_EXECUTION_FAILED: {
      const payload = error.error_payload as BaseErrorPayload;
      const displayName =
        typeof payload?.display_name === 'string' ? payload.display_name : '';
      // step_id / workflow_id are only present on payloads emitted by newer
      // backends. Only offer the "View the failed step" deep link when both
      // are present — otherwise the button would route nowhere.
      const hasStepLink = Boolean(payload?.step_id && payload?.workflow_id);
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={payload}
          showDeepLink={hasStepLink}
        >
          <Typography variant={fontVariant}>
            {displayName
              ? t('recommendedFix.solveWidget.codeExecutionFailed.step1Named', {
                  displayName,
                })
              : t('recommendedFix.solveWidget.codeExecutionFailed.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveWidget.codeExecutionFailed.step2')}
          </Typography>
        </HowToFixSection>
      );
    }
    default:
      return null;
  }
};

const getIntegrationsRecommendedFix = (
  error: ErrorQueryEvent,
  fontVariant: FontVariant = 'font16',
) => {
  const statusCode = error.status_code;
  if (!statusCode) {
    return null;
  }

  if (statusCode === 401 || statusCode === 403) {
    return (
      <PlainTextFix
        fontVariant={fontVariant}
        text={t('recommendedFix.integrations.insufficientPermissions')}
      />
    );
  }

  if (statusCode === 429) {
    return (
      <PlainTextFix
        fontVariant={fontVariant}
        text={t('recommendedFix.integrations.rateLimited')}
      />
    );
  }

  return null;
};

const getSolveEmailRecommendedFix = (
  error: ErrorQueryEvent,
  fontVariant: FontVariant = 'font16',
) => {
  switch (error.error_message) {
    case WEBHOOK_REQUEST_VALIDATION_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            <Trans
              components={{
                lnk: (
                  <a
                    href='https://support.forethought.ai/hc/en-us/articles/30458719071635-Create-a-custom-Callout-flow-in-Salesforce-for-Solve-Email'
                    rel='noopener noreferrer'
                    target='_blank'
                  />
                ),
              }}
              i18nKey='recommendedFix.solveEmail.webhookRequestValidationError'
              ns='events'
              t={t}
            />
          </Typography>
        </HowToFixSection>
      );
    case EMAIL_CONVERSATION_MISSING_CONTEXT_VARIABLES:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveEmail.emailConversationMissingContextVariables.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveEmail.emailConversationMissingContextVariables.step2',
            )}
          </Typography>
        </HowToFixSection>
      );
    case EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveEmail.emailConversationMissingStaticArticle.step1',
            )}
          </Typography>
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveEmail.emailConversationMissingStaticArticle.step2',
            )}
          </Typography>
        </HowToFixSection>
      );
    case TICKET_UPDATE_STEP_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveEmail.ticketUpdateStepError')}
          </Typography>
        </HowToFixSection>
      );
    case MISSING_ZENDESK_TICKET_FIELD:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveEmail.missingZendeskTicketField')}
          </Typography>
        </HowToFixSection>
      );
    case ZENDESK_API_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            <Trans
              components={{
                lnk: (
                  <a
                    href='https://developer.zendesk.com/api-reference/sales-crm/errors'
                    rel='noopener noreferrer'
                    target='_blank'
                  />
                ),
              }}
              i18nKey='recommendedFix.solveEmail.zendeskApiError'
              ns='events'
              t={t}
            />
          </Typography>
        </HowToFixSection>
      );
    case EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE_TRANSLATION:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t(
              'recommendedFix.solveEmail.emailConversationMissingStaticArticleTranslation',
            )}
          </Typography>
        </HowToFixSection>
      );
    case HALOCRM_API_ERROR:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.solveEmail.halocrmApiError')}
          </Typography>
        </HowToFixSection>
      );
    default:
      return null;
  }
};

const getAssistRecommendedFix = (
  error: ErrorQueryEvent,
  fontVariant: FontVariant = 'font16',
) => {
  switch (error.error_message) {
    case MISSING_CONTEXT_VARIABLE: {
      const payload = error.error_payload as MissingContextVariablePayload;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography variant={fontVariant}>
            {t('recommendedFix.assist.missingContextVariable.toResolve')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('missingContextVariableFix.method1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.assist.missingContextVariable.step2')}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <AssistDeepLink
              stepId={payload.step_id}
              workflowId={payload.workflow_id}
            />
          </Box>
        </Box>
      );
    }
    case TICKET_FETCH_FAILED:
      return (
        <HowToFixSection
          channel={error.channel}
          error_payload={error.error_payload as BaseErrorPayload}
          showDeepLink={false}
        >
          <Typography variant={fontVariant}>
            {t('recommendedFix.assist.ticketFetchFailed.step1')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.assist.ticketFetchFailed.step2')}
          </Typography>
          <Typography variant={fontVariant}>
            {t('recommendedFix.assist.ticketFetchFailed.step3')}
          </Typography>
        </HowToFixSection>
      );
    default:
      return null;
  }
};

const getTriageRecommendedFix = (
  error: ErrorQueryEvent,
  fontVariant: FontVariant = 'font16',
) => {
  const steps = getTriageFixSteps(error.error_message);

  if (!steps.length) {
    return null;
  }

  return <TriageErrorFix fontVariant={fontVariant} steps={steps} />;
};

const getTriageFixSteps = (errorMessage: string): string[] => {
  switch (errorMessage) {
    case TRIAGE_RESTRICTED_PICKLIST_VALUE_REJECTED:
      return [
        t('recommendedFix.triage.restrictedPicklistValueRejected.step1'),
        t('recommendedFix.triage.restrictedPicklistValueRejected.step2'),
      ];
    case TRIAGE_FIELD_TYPE_MISMATCH:
      return [
        t('recommendedFix.triage.fieldTypeMismatch.step1'),
        t('recommendedFix.triage.fieldTypeMismatch.step2'),
      ];
    case TRIAGE_FIELD_VALIDATION_RULE_REJECTED:
      return [
        t('recommendedFix.triage.fieldValidationRuleRejected.step1'),
        t('recommendedFix.triage.fieldValidationRuleRejected.step2'),
      ];
    case TRIAGE_FIELD_NOT_WRITABLE:
      return [
        t('recommendedFix.triage.fieldNotWritable.step1'),
        t('recommendedFix.triage.fieldNotWritable.step2'),
      ];
    case TRIAGE_HELPDESK_REJECTED_VALUE:
      return [
        t('recommendedFix.triage.helpdeskRejectedValue.step1'),
        t('recommendedFix.triage.helpdeskRejectedValue.step2'),
        t('recommendedFix.triage.helpdeskRejectedValue.step3'),
      ];
    case TRIAGE_TICKET_CLOSED_FOR_UPDATES:
      return [
        t('recommendedFix.triage.ticketClosedForUpdates.step1'),
        t('recommendedFix.triage.ticketClosedForUpdates.step2'),
        t('recommendedFix.triage.ticketClosedForUpdates.step3'),
      ];
    case TRIAGE_REQUIRED_FIELD_MISSING:
      return [
        t('recommendedFix.triage.requiredFieldMissing.step1'),
        t('recommendedFix.triage.requiredFieldMissing.step2'),
        t('recommendedFix.triage.requiredFieldMissing.step3'),
      ];
    case TRIAGE_VALUE_NOT_A_CONFIGURED_OPTION:
      return [
        t('recommendedFix.triage.valueNotAConfiguredOption.step1'),
        t('recommendedFix.triage.valueNotAConfiguredOption.step2'),
        t('recommendedFix.triage.valueNotAConfiguredOption.step3'),
      ];
    case TRIAGE_OUTPUT_VALUE_CAST_FAILED:
      return [
        t('recommendedFix.triage.outputValueCastFailed.step1'),
        t('recommendedFix.triage.outputValueCastFailed.step2'),
      ];
    case TRIAGE_TICKET_FETCH_FAILED:
      return [
        t('recommendedFix.triage.ticketFetchFailed.step1'),
        t('recommendedFix.triage.ticketFetchFailed.step2'),
      ];
    default:
      return [];
  }
};
